import { create } from 'zustand'
import type { ChannelId, ControllerKind, DocumentModel, GenResult, Vec2 } from '../core/types'
import { defaultDocument } from '../core/types'

export type PendingAdd = {
  target: 'controller' | 'path' | 'boundary'
  channel: ChannelId // controller target only
  kind: ControllerKind // paths use 'curve' | 'polyline'; boundary uses 'polyline'
  firstPoint: Vec2 | null // line placement: first click
  nodes: Vec2[] // curve/polyline placement: accumulated clicks
} | null

// A history entry stores the document as it was BEFORE the labeled action ran.
export interface HistoryEntry {
  doc: DocumentModel
  label: string
}

export interface View {
  scale: number // screen px per mm
  tx: number
  ty: number
}

export type ToastKind = 'info' | 'ok' | 'warn' | 'error'

export interface Toast {
  id: number
  kind: ToastKind
  text: string
}

// At most one overlay is open at a time; Esc closes it.
export type Overlay =
  | null
  | 'palette'
  | 'export'
  | 'history'
  | 'shortcuts'
  | 'shapeEditor'
  | 'gallery'
  | 'about'
  | 'scale'

export type WorkerHealth = 'idle' | 'busy' | 'restarted'

const UNDO_CAP = 100
const SEED_HISTORY_CAP = 8
export const THEME_KEY = 'ventholes-theme'
export const LOCALE_KEY = 'ventholes-locale'
export const SNAP_KEY = 'ventholes-snap'

function initialSnap(): boolean {
  try {
    return localStorage.getItem(SNAP_KEY) !== 'off'
  } catch {
    return true
  }
}

function initialLocale(): 'en' | 'de' | 'ja' {
  try {
    const saved = localStorage.getItem(LOCALE_KEY)
    if (saved === 'en' || saved === 'de' || saved === 'ja') return saved
  } catch {
    // Non-browser environment.
  }
  return 'en'
}

export type ThemeId = 'dark' | 'light'

function initialTheme(): ThemeId {
  try {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark') return saved
    if (typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light'
    }
  } catch {
    // Non-browser environment (tests): fall through.
  }
  return 'dark'
}

function applyTheme(theme: ThemeId) {
  try {
    document.documentElement.dataset.theme = theme
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    // Non-browser environment: nothing to apply.
  }
}

interface AppState {
  doc: DocumentModel
  result: GenResult | null
  past: HistoryEntry[]
  future: HistoryEntry[]
  gestureBase: DocumentModel | null
  gestureLabel: string
  gesturing: boolean
  perfMode: boolean
  snap: boolean
  activeChannel: ChannelId
  selectedController: string | null
  selectedPath: string | null
  pendingAdd: PendingAdd
  view: View | null
  fitTick: number
  theme: ThemeId
  locale: 'en' | 'de' | 'ja'
  overlay: Overlay
  toasts: Toast[]
  savedAt: number | null
  seedHistory: number[]
  workerHealth: WorkerHealth

  commit: (label: string, up: (d: DocumentModel) => void) => void
  live: (up: (d: DocumentModel) => void) => void
  beginGesture: (label: string) => void
  endGesture: () => void
  undo: () => void
  redo: () => void
  jumpBy: (delta: number) => void
  loadDocument: (doc: DocumentModel, label?: string) => void
  resetDocument: () => void
  setResult: (r: GenResult) => void
  setPerfMode: (on: boolean) => void
  setSnap: (on: boolean) => void
  setActiveChannel: (c: ChannelId) => void
  setSelectedController: (id: string | null) => void
  setSelectedPath: (id: string | null) => void
  setPendingAdd: (p: PendingAdd) => void
  setView: (v: View | null) => void
  requestFit: () => void
  setTheme: (t: ThemeId) => void
  toggleTheme: () => void
  setLocale: (l: 'en' | 'de' | 'ja') => void
  setOverlay: (o: Overlay) => void
  pushToast: (text: string, kind?: ToastKind) => void
  dismissToast: (id: number) => void
  markSaved: () => void
  rememberSeed: (seed: number) => void
  setWorkerHealth: (h: WorkerHealth) => void
}

function clone(doc: DocumentModel): DocumentModel {
  return structuredClone(doc)
}

let toastSeq = 1

export const useStore = create<AppState>((set, get) => ({
  doc: defaultDocument(),
  result: null,
  past: [],
  future: [],
  gestureBase: null,
  gestureLabel: '',
  gesturing: false,
  perfMode: false,
  snap: initialSnap(),
  activeChannel: 'size',
  selectedController: null,
  selectedPath: null,
  pendingAdd: null,
  view: null,
  fitTick: 0,
  theme: initialTheme(),
  locale: initialLocale(),
  overlay: null,
  toasts: [],
  savedAt: null,
  seedHistory: [],
  workerHealth: 'idle',

  commit: (label, up) => {
    const { doc, past } = get()
    const next = clone(doc)
    up(next)
    set({
      doc: next,
      past: [...past.slice(-UNDO_CAP + 1), { doc, label }],
      future: [],
    })
  },

  live: (up) => {
    const next = clone(get().doc)
    up(next)
    set({ doc: next })
  },

  beginGesture: (label) => {
    const { gesturing, doc } = get()
    if (gesturing) return
    set({ gestureBase: clone(doc), gestureLabel: label, gesturing: true })
  },

  endGesture: () => {
    const { gestureBase, gestureLabel, past, doc } = get()
    if (!gestureBase) {
      set({ gesturing: false })
      return
    }
    const changed = JSON.stringify(gestureBase) !== JSON.stringify(doc)
    set({
      gestureBase: null,
      gestureLabel: '',
      gesturing: false,
      past: changed
        ? [...past.slice(-UNDO_CAP + 1), { doc: gestureBase, label: gestureLabel }]
        : past,
      future: changed ? [] : get().future,
    })
  },

  undo: () => {
    const { past, future, doc } = get()
    if (past.length === 0) return
    const entry = past[past.length - 1]
    set({
      doc: entry.doc,
      past: past.slice(0, -1),
      future: [{ doc, label: entry.label }, ...future].slice(0, UNDO_CAP),
    })
  },

  redo: () => {
    const { past, future, doc } = get()
    if (future.length === 0) return
    const entry = future[0]
    set({
      doc: entry.doc,
      future: future.slice(1),
      past: [...past.slice(-UNDO_CAP + 1), { doc, label: entry.label }],
    })
  },

  jumpBy: (delta) => {
    const { undo, redo } = get()
    for (let i = 0; i < Math.abs(delta); i++) {
      if (delta < 0) undo()
      else redo()
    }
  },

  loadDocument: (newDoc, label = 'Load design') => {
    const { doc, past } = get()
    set({
      doc: newDoc,
      past: [...past.slice(-UNDO_CAP + 1), { doc, label }],
      future: [],
      selectedController: null,
      pendingAdd: null,
    })
  },

  resetDocument: () => {
    get().loadDocument(defaultDocument(), 'Reset design')
  },

  setResult: (r) => set({ result: r }),
  setPerfMode: (on) => set({ perfMode: on }),
  setSnap: (on) => {
    try {
      localStorage.setItem(SNAP_KEY, on ? 'on' : 'off')
    } catch {
      // Non-browser environment.
    }
    set({ snap: on })
  },
  setActiveChannel: (c) => set({ activeChannel: c, selectedController: null, pendingAdd: null }),
  setSelectedController: (id) => set({ selectedController: id }),
  setSelectedPath: (id) => set({ selectedPath: id }),
  setPendingAdd: (p) => set({ pendingAdd: p }),
  setView: (v) => set({ view: v }),
  requestFit: () => set({ fitTick: get().fitTick + 1 }),

  setTheme: (t) => {
    applyTheme(t)
    set({ theme: t })
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  setLocale: (l) => {
    try {
      localStorage.setItem(LOCALE_KEY, l)
    } catch {
      // Non-browser environment.
    }
    set({ locale: l })
  },

  setOverlay: (o) => set({ overlay: o }),

  pushToast: (text, kind = 'info') => {
    const id = toastSeq++
    set({ toasts: [...get().toasts, { id, kind, text }] })
    setTimeout(() => get().dismissToast(id), 4200)
  },
  dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  markSaved: () => set({ savedAt: Date.now() }),

  rememberSeed: (seed) => {
    const { seedHistory } = get()
    if (seedHistory[0] === seed) return
    set({ seedHistory: [seed, ...seedHistory.filter((s) => s !== seed)].slice(0, SEED_HISTORY_CAP) })
  },

  setWorkerHealth: (h) => set({ workerHealth: h }),
}))

export function newControllerId(): string {
  return 'c' + Math.random().toString(36).slice(2, 10)
}
